// src/components/popups/PostActionDrawer.js

import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './PostActionDrawer.css';
import { useTheme } from '../../context/ThemeContext';

// light icons
import friendIcon from '../../assets/friend-icon.png';
import reportIcon from '../../assets/report-icon.png';
import pinIcon from '../../assets/pin-icon.png';
import campaignIcon from '../../assets/campaign-icon.png';
import editIcon from '../../assets/edit-icon.png';
import blockIcon from '../../assets/block-icon.png';

// dark icons
import friendIconDark from '../../assets/frienddark-icon.png';
import reportIconDark from '../../assets/reportdark-icon.png';
import campaignIconDark from '../../assets/campaigndark-icon.png';
import editIconDark from '../../assets/editdark-icon.png';
import blockIconDark from '../../assets/blockdark-icon.png';

export default function PostActionDrawer({
  isOpen,
  onClose,

  // viewer/post relationship
  isOwner = false,
  isFollowing = false,
  followsMe = false,
  targetUsername = '',
  isBlocked = false,
  blockActionLoading = false,

  // actions
  onToggleFollow,
  onToggleBlock,
  onReport,
  onDelete,
  onEdit,

  // pin (admin-only UI)
  pinned = false,
  onPinToggle,
  showPinControls = false,

  // stake (public for any logged-in user)
  showStakeOption = false,
  canStake = undefined, // back-compat
  onStakeClick,
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const icons = React.useMemo(
    () => ({
      friend: isDark ? friendIconDark : friendIcon,
      report: isDark ? reportIconDark : reportIcon,
      campaign: isDark ? campaignIconDark : campaignIcon,
      edit: isDark ? editIconDark : editIcon,
      block: isDark ? blockIconDark : blockIcon,
      pin: pinIcon, // keep as-is unless you add a pindark icon later
    }),
    [isDark]
  );

  // Final stake gate
  const canStakeFinal =
    typeof showStakeOption === 'boolean' ? showStakeOption : Boolean(canStake);

  const showPinFinal = Boolean(showPinControls);


  // --- History trap flags ---
  const pushedRef = useRef(false);
  const closingFromButtonRef = useRef(false);

  // ✅ Keep latest onClose without making the drawer effect restart on Home re-renders.
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  /**
   * Close helper
   * - manual: user clicked something
   * - skipHistoryBack: do NOT call window.history.back()
   */
  const safeClose = useCallback(({ manual, skipHistoryBack = false }) => {
    if (manual) closingFromButtonRef.current = true;

    try {
      onCloseRef.current?.();
    } catch {}

    if (
      !skipHistoryBack &&
      manual &&
      pushedRef.current &&
      window.history.state?.bfDrawer === 'postaction'
    ) {
      try {
        window.history.back();
      } catch {}

      pushedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // push dummy history entry only once per real open
    try {
      window.history.pushState({ bfDrawer: 'postaction' }, '');
      pushedRef.current = true;
    } catch {}

    // lock background scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onPopState = () => {
      if (closingFromButtonRef.current) {
        closingFromButtonRef.current = false;
        return;
      }

      safeClose({ manual: false });
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('popstate', onPopState);

      // ✅ Only clean our drawer history entry when the drawer actually closes/unmounts.
      // This effect no longer restarts just because Home re-rendered.
      if (pushedRef.current && window.history.state?.bfDrawer === 'postaction') {
        try {
          window.history.back();
        } catch {}

        pushedRef.current = false;
      }
    };
  }, [isOpen, safeClose]);


  if (!isOpen) return null;

  const followLabel = isOwner
    ? null
    : isFollowing
    ? `Unfollow @${targetUsername}`
    : followsMe
    ? `Follow back @${targetUsername}`
    : `Follow @${targetUsername}`;

  const blockLabel = isBlocked
    ? `Unblock @${targetUsername}`
    : `Block @${targetUsername}`;


return createPortal(

    <div className="pad-backdrop" onClick={() => safeClose({ manual: true })}>
      <div
        className="pad-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="pad-grabber" aria-hidden="true" />

        {isOwner ? (
          <>
            {/* Admin-only: Pin / Unpin */}
            {showPinFinal && (
              <button
                className="pad-item"
                onClick={() => {
                  safeClose({ manual: true });
                  onPinToggle?.();
                }}
              >
                <img src={icons.pin} alt="" className="pad-item-icon" />
                <span>{pinned ? 'Unpin from top' : 'Pin to top'}</span>
              </button>
            )}

            {/* Stake to boost */}
            {canStakeFinal && (
              <button
                className="pad-item"
                onClick={(e) => {
                  e.stopPropagation();
                  safeClose({ manual: true, skipHistoryBack: true });
                  onStakeClick?.();
                }}
              >
                <img src={icons.campaign} alt="" className="pad-item-icon" />
                <span>Stake to boost</span>
              </button>
            )}

            {/* Edit post */}
            <button
              className="pad-item"
              onClick={(e) => {
                e.stopPropagation();
                safeClose({ manual: true, skipHistoryBack: true });
                onEdit?.();
              }}
            >
              <img src={icons.edit} alt="" className="pad-item-icon" />
              <span>Edit post</span>
            </button>

            {/* Delete */}
            <button
              className="pad-item danger"
              onClick={() => {
                safeClose({ manual: true });
                onDelete?.();
              }}
            >
              Delete Post
            </button>

            {/* Cancel */}
            <button className="pad-item" onClick={() => safeClose({ manual: true })}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {/* Admin-only pin for non-owner */}
            {showPinFinal && (
              <button
                className="pad-item"
                onClick={() => {
                  safeClose({ manual: true });
                  onPinToggle?.();
                }}
              >
                <img src={icons.pin} alt="" className="pad-item-icon" />
                <span>{pinned ? 'Unpin from top' : 'Pin to top'}</span>
              </button>
            )}

            {!!followLabel && (
              <button
                className="pad-item"
                onClick={() => {
                  safeClose({ manual: true });
                  onToggleFollow?.();
                }}
              >
                <img src={icons.friend} alt="" className="pad-item-icon" />
                <span>{followLabel}</span>
              </button>
            )}

            {canStakeFinal && (
              <button
                className="pad-item"
                onClick={(e) => {
                  e.stopPropagation();
                  safeClose({ manual: true, skipHistoryBack: true });
                  onStakeClick?.();
                }}
              >
                <img src={icons.campaign} alt="" className="pad-item-icon" />
                <span>Stake to boost</span>
              </button>
            )}

            <button
              className="pad-item"
              disabled={blockActionLoading}
              onClick={(e) => {
                e.stopPropagation();

                // Post.js now closes this drawer and opens BlockConfirmDrawer.
                onToggleBlock?.();
              }}
            >
              <img src={icons.block} alt="" className="pad-item-icon" />
              <span>{blockActionLoading ? 'Please wait...' : blockLabel}</span>
            </button>

            <button
              className="pad-item"
              onClick={() => {
                safeClose({ manual: true });
                onReport?.();
              }}
            >
              <img src={icons.report} alt="" className="pad-item-icon" />
              <span>Report</span>
            </button>

            <button className="pad-item" onClick={() => safeClose({ manual: true })}>
              Cancel
            </button>
          </>
        )}
      </div>

    </div>,
    document.body
  );
}
